// Закупки и отгрузка: правила разбора, общие для двух разделов CRM.
//
// ПОЧЕМУ ОДИН МОДУЛЬ НА ДВА ЭКРАНА. «Кабинет закупщика» и «Отгрузка» — это два конца одной
// цепочки снабжения: заявка на оплату поставщику → заказ поставщику в 1С → приход на склад →
// заказ клиента → реализация. Оба экрана разбирают одни и те же документы 1С УТ, один и тот же
// справочник складов и одну и ту же связку «сделка 1С ↔ сделка Bitrix». Разложить это по двум
// файлам значило бы завести две копии разбора, которые разойдутся в первый же месяц.
//
// Здесь только чистые функции и константы: ни React, ни сети (сеть — в api/purchasing.ts).
//
// ─────────────────────── ОТКУДА БЕРУТСЯ ДАННЫЕ ───────────────────────
//
// Закупщик — смарт-процесс 1160 «Счета на оплату 2.0» (10 992 записи в ядре на 26.07.2026).
// Это НАШИ заявки на оплату поставщикам: карточку рождает бизнес-процесс списка 18, портал 55
// дописывает компанию и сделку. Стадии 1160 и есть рабочий конвейер закупщика — от
// «Предварительного счёта» до «Ждём закрывашки».
//
// Отгрузка — 1С УТ через прокси ядра: Document_ЗаказКлиента (что продано и ждёт отправки) и
// Document_РеализацияТоваровУслуг (что реально списано со склада). Смарт-процесс 1188
// «Логист (отправка)» для этого НЕ годится, и это проверено, а не предположено: в ядре у него
// шесть записей, все от 01.08.2025, все с названием «ОТПРАВКА», все на одну сделку 87451 и
// с тестовыми значениями («Габариты: 111», трек «555-12341234»). Бизнес-процессов у 1188 нет
// вовсе, единственный источник — портал 55 (bpLogUpsert), а он пишет туда четыре поля от
// случая к случаю. Экран над шестью тестовыми строками показывал бы, что отгрузок нет.

import { daysPast, isoDay, todayIso } from './format'
import { toNumber } from './money'
import type { RefsApi } from './refs'
import { chunk, isRef, odataPath, q1c } from './stock'

// ════════════════════════ ЧАСТЬ 1. Заявки на оплату (смарт-процесс 1160) ════════════════════════

export const PUR_TYPE_ID = 1160
/** Область справочника полей ядра. Названия полей и значения перечислений берём оттуда. */
export const PUR_ENTITY = 'item:1160'

/**
 * Коды полей 1160. Зашиты осознанно — в отличие от общего экрана смарт-процессов, где колонки
 * подбираются по справочнику: здесь экран не «показывает поля», а СЧИТАЕТ по ним (просрочку
 * планового платежа, фазу конвейера, суммы по валютам). Ошибиться в коде нельзя, а угадать
 * «по смыслу» — значит молча показать пустые колонки. Каждый код проверен на живых данных
 * 26.07.2026, в скобках — заполненность по справочнику ядра.
 */
export const PUR = {
  /** Номер счёта поставщика (99 %). */
  invoiceNo: 'ufCrmPropertyNumber',
  /** Дата счёта (94 %). */
  invoiceDate: 'ufCrmPropertyDate',
  /** Сумма счёта (100 %). Число, валюта — в системном currencyId. */
  sum: 'ufCrmPropertySum',
  /** НДС (39 %). */
  vat: 'ufCrm62_1764237519276',
  /** Плановая дата платежа (100 %) — единственный срок, который есть у всех заявок. */
  planDate: 'ufCrmPropertyPlanovayaDataPlatezha',
  /** Дата оплаты (63 %). Есть — значит деньги ушли. */
  payDate: 'ufCrmPropertyDatePay',
  /** Утверждён: «Не согласовано / Отказано / Согласовано / андрей» (98 %). */
  approved: 'ufCrmPropertyIsApproved',
  /** Утверждён 2: до какого этапа согласования дошло (90 %). */
  approvedStage: 'ufCrm_PROPERTY_UTVERZHDEN_2',
  /** Оплачен: да/нет (98 %). */
  paid: 'ufCrmPropertyIsPayed',
  /** Для какого отдела (97 %, 27 значений). */
  dept: 'ufCrmPropertyOtdel',
  /** Статья расходов (99 %, 13 значений). */
  expense: 'ufCrmPropertyTestStatiRaskhodov',
  /** Расширенная статья расходов (73 %, 76 значений). */
  expenseExt: 'ufCrm_PROPERTY_STATI_RASKHODOV2',
  /** От какой компании платим (99 %, 13 значений). */
  payer: 'ufCrmPropertyOtKakoyKompaniiOplachivaem',
  /** За что, коротко (92 %). */
  purpose: 'ufCrmPropertyName',
  /** Комментарии (96 %). */
  comment: 'ufCrmPropertyKommentarii',
  /** ID элемента списка 18, из которого карточку родил бизнес-процесс (92 %). */
  listElement: 'ufCrm62_1755176521',
  /** Ссылка на этот элемент в Bitrix (90 %). */
  listUrl: 'ufCrm62_1755154640',
  /**
   * Номер заказа поставщику в 1С — единственная машинная связка 1160 ↔ УТ.
   * Заполнен у 182 карточек из 11 046, это 1,6 % (на 28.07.2026).
   */
  utOrderNo: 'ufCrm62_1778848370',
  /** Утверждающий 1 этапа: id сотрудника Bitrix (65 %). */
  approver: 'ufCrm62_1763389945',
  /** Сделки — множественное поле-привязка (52 %), рядом с системным parentId2 (51 %). */
  deals: 'ufCrm62_1738592028',
} as const

/**
 * Что просим у ядра для списка. Явный select обязателен: у 1160 сто двадцать три поля,
 * а страница — пятьдесят сырых записей.
 */
export const PUR_LIST_SELECT: string[] = [
  'id', 'title', 'stageId', 'companyId', 'parentId2', 'assignedById',
  'opportunity', 'currencyId', 'createdTime', 'updatedTime',
  PUR.invoiceNo, PUR.invoiceDate, PUR.sum, PUR.vat, PUR.planDate, PUR.payDate,
  PUR.approved, PUR.approvedStage, PUR.paid, PUR.dept, PUR.expense, PUR.expenseExt,
  PUR.payer, PUR.purpose, PUR.comment, PUR.listElement, PUR.listUrl, PUR.utOrderNo,
  PUR.approver, PUR.deals,
]

/**
 * Фазы конвейера закупщика — группировка стадий 1160 по вопросу «кто сейчас должен
 * пошевелиться».
 *
 * Стадии зашиты кодами, потому что группировки по смыслу в справочнике Bitrix НЕТ: там у
 * стадии есть только название и семантика (успех/провал/в работе), а «ждёт согласования»
 * против «согласовано, ждёт денег» из семантики не выводится — обе «в работе». Названия при
 * этом всё равно берём из справочника ядра (refs.itemStageName), поэтому переименование
 * стадии в Bitrix экран увидит; а вот добавление новой стадии — нет, и экран об этом
 * честно пишет (см. unknownStages ниже).
 *
 * Счётчики на живых данных 26.07.2026: согласование 240, ждёт оплаты 101,
 * оплачено 1803, закрыто 7029, отклонено 1819 — итого 10 992.
 */
export interface PayPhase {
  key: 'approval' | 'topay' | 'paid' | 'done' | 'rejected'
  label: string
  hint: string
  stages: string[]
  /** Подсвечивать плитку: тут заявка ждёт живого человека. */
  hot?: boolean
  /** Заявка ещё в работе: деньги не ушли или не закрыта. */
  open?: boolean
}

export const PAY_PHASES: PayPhase[] = [
  {
    key: 'approval',
    label: 'Ждёт согласования',
    hint: 'Исправление счёта, предварительный счёт, утверждение руководителя, согласование '
      + 'у Андрея — заявка стоит на живом человеке.',
    stages: ['DT1160_79:NEW', 'DT1160_79:CLIENT', 'DT1160_79:PREPARATION', 'DT1160_79:UC_QV3IQW'],
    hot: true,
    open: true,
  },
  {
    key: 'topay',
    label: 'Согласовано, ждёт оплаты',
    hint: 'Утверждено, деньги ещё не ушли: «В ожидании оплаты» и «Частично оплачен».',
    stages: ['DT1160_79:UC_5KORKR', 'DT1160_79:UC_NKIAEX'],
    hot: true,
    open: true,
  },
  {
    key: 'paid',
    label: 'Оплачено',
    hint: 'Деньги ушли: «Оплачен» и «Ждём закрывашки» (нет закрывающих документов).',
    stages: ['DT1160_79:UC_04JYFT', 'DT1160_79:UC_G8BCCL'],
  },
  {
    key: 'done',
    label: 'Закрыто',
    hint: 'Стадия «Успех»: оплачено и документы получены.',
    stages: ['DT1160_79:SUCCESS'],
  },
  {
    key: 'rejected',
    label: 'Отклонено',
    hint: 'Стадия «Счет отклонен».',
    stages: ['DT1160_79:FAIL'],
  },
]

export type PayPhaseKey = PayPhase['key']

/** Все стадии, которые мы знаем. Нужен, чтобы поймать появление новой стадии в Bitrix. */
export const KNOWN_PAY_STAGES: string[] = PAY_PHASES.flatMap((p) => p.stages)

/** Стадии «заявка ещё в работе» — для фильтра «только открытые» и для подсчёта просрочки. */
export const OPEN_PAY_STAGES: string[] = PAY_PHASES.filter((p) => p.open).flatMap((p) => p.stages)

/**
 * Фильтр «просрочен плановый платёж» для ядра — ровно то же условие, что даёт `overdue`
 * у строки таблицы: открытая стадия, пустая дата оплаты, плановая дата не позже сегодняшней.
 *
 * Одна функция на плитку-счётчик и на галочку в фильтрах намеренно: разойдись они, и человек
 * увидит «просрочено 241» над таблицей, в которой просроченных 226. На живых данных
 * 26.07.2026 это условие даёт 226 заявок из 341 открытой.
 */
export const overdueFilter = (today: string): Record<string, unknown> => ({
  stageId: OPEN_PAY_STAGES,
  [PUR.payDate]: '',
  [`<=${PUR.planDate}`]: today,
})

const PHASE_BY_STAGE = new Map<string, PayPhase>(
  PAY_PHASES.flatMap((p) => p.stages.map((s) => [s, p] as [string, PayPhase])),
)

export const phaseOfStage = (stageId?: string | null): PayPhase | undefined =>
  PHASE_BY_STAGE.get(String(stageId ?? ''))

/**
 * Стадии 1160, которые есть в справочнике ядра, но не разложены нами по фазам.
 * Пустой массив — наша группировка полная. Непустой — экран обязан сказать, что часть
 * заявок в плитки не попала: молча потерянная стадия это молча потерянные деньги.
 */
export function unknownStages(refs: RefsApi): Array<{ id: string; name: string }> {
  if (!refs.hasItemStages(PUR_TYPE_ID)) return []
  return refs.itemStageOptions(PUR_TYPE_ID)
    .filter((s) => !PHASE_BY_STAGE.has(s.id))
    .map((s) => ({ id: s.id, name: s.name }))
}

/** Строка списка заявок — всё уже разобрано, экран только раскладывает по ячейкам. */
export interface PayRequestView {
  id: string
  title: string
  /** Номер счёта поставщика. */
  invoiceNo: string
  invoiceDate: string
  sum: number | null
  currency: string
  vat: number | null
  /** Плановая дата платежа, «ГГГГ-ММ-ДД». Пусто — срока нет. */
  planDay: string
  /** Дней от планового платежа до сегодня: >0 — просрочен, <0 — впереди. null — срока нет. */
  planOverdueDays: number | null
  /** Дата фактической оплаты, «ГГГГ-ММ-ДД». Непусто — деньги ушли. */
  payDay: string
  /** Оплачено. Считаем ТОЛЬКО по дате оплаты — почему, см. paidFlagBroken ниже. */
  paid: boolean
  /** Флаг «Оплачен» из Bitrix. Сам по себе ничего не значит, см. paidDisputed. */
  paidFlag: boolean
  /** Флаг «Оплачен: Да» стоит, а даты оплаты нет, и заявка на открытой стадии. */
  paidDisputed: boolean
  stageId: string
  stageName: string
  phase?: PayPhase
  /** Просрочен плановый платёж И заявка ещё открыта. Только это стоит подсвечивать. */
  overdue: boolean
  companyId: string
  /** Сделка: системный родитель, а если пусто — первая из поля-привязки «Сделки». */
  dealId: string
  /** true — сделка взята из поля-привязки, а не из системного родителя. */
  dealFromField: boolean
  assignedById: string
  approverId: string
  dept: string
  expense: string
  expenseExt: string
  payer: string
  approved: string
  approvedStage: string
  purpose: string
  comment: string
  /** Ссылка на элемент списка 18 в Bitrix — там файл счёта и ход согласования. */
  listUrl: string
  utOrderNo: string
}

/** Значение перечисления текстом. Нет справочника — пусто: код «4258» человеку не поможет. */
type EnumText = (code: string, value: unknown) => string

const str = (v: unknown): string => {
  if (v === null || v === undefined) return ''
  const s = String(v).trim()
  return s === '0' ? '' : s
}

/** Первый непустой id из множественного поля-привязки. Bitrix отдаёт голые id строками. */
function firstRefId(v: unknown): string {
  const list = Array.isArray(v) ? v : [v]
  for (const x of list) {
    // поле-привязка иногда приходит как «D_111371», иногда голым числом — берём цифры
    const digits = String(x ?? '').replace(/\D/g, '')
    if (digits && digits !== '0') return digits
  }
  return ''
}

/**
 * Одна запись 1160 → строка экрана.
 *
 * `enumText` — из useFields(PUR_ENTITY): отдел, статья расходов и признаки утверждения
 * приходят числовыми id перечислений, и без справочника показывать их нельзя.
 */
export function payRequestView(
  item: Record<string, unknown>, refs: RefsApi, enumText: EnumText, today = todayIso(),
): PayRequestView {
  const stageId = String(item.stageId ?? '')
  const phase = phaseOfStage(stageId)
  const planDay = isoDay(item[PUR.planDate]) ?? ''
  const payDay = isoDay(item[PUR.payDate]) ?? ''
  // Оплачено = есть ДАТА оплаты. Флаг «Оплачен» в подсчёт не берём: на живых данных
  // 26.07.2026 он стоит «Да» у 9975 карточек из 10 992 — в том числе у 30 заявок,
  // которые ещё висят на согласовании и денег по которым не ушло. Признак, верный у 91 %
  // записей независимо от их состояния, не признак, а константа. Дата оплаты заполнена
  // у 63 % и согласуется со стадией: на 28.07.2026 у 1106 из 1148 карточек на стадии
  // «Оплачен» она есть.
  const payFlag = enumText(PUR.paid, item[PUR.paid]) === 'Да'
  const paid = !!payDay
  const planOverdueDays = planDay ? daysPast(planDay, today) : null
  const parentDeal = str(item.parentId2)
  const fieldDeal = firstRefId(item[PUR.deals])

  return {
    id: String(item.id ?? ''),
    title: String(item.title ?? ''),
    invoiceNo: str(item[PUR.invoiceNo]),
    invoiceDate: isoDay(item[PUR.invoiceDate]) ?? '',
    // Сумма счёта заполнена у 100 % записей, но если её вдруг нет — берём стандартную.
    sum: toNumber(item[PUR.sum]) ?? toNumber(item.opportunity),
    currency: String(item.currencyId || 'RUB'),
    vat: toNumber(item[PUR.vat]),
    planDay,
    planOverdueDays,
    payDay,
    paid,
    paidFlag: payFlag,
    paidDisputed: payFlag && !payDay && !!phase?.open,
    stageId,
    stageName: refs.itemStageName(stageId),
    phase,
    overdue: !!phase?.open && !paid && planOverdueDays !== null && planOverdueDays > 0,
    companyId: str(item.companyId),
    dealId: parentDeal || fieldDeal,
    dealFromField: !parentDeal && !!fieldDeal,
    assignedById: str(item.assignedById),
    approverId: str(item[PUR.approver]),
    dept: enumText(PUR.dept, item[PUR.dept]),
    expense: enumText(PUR.expense, item[PUR.expense]),
    expenseExt: enumText(PUR.expenseExt, item[PUR.expenseExt]),
    payer: enumText(PUR.payer, item[PUR.payer]),
    approved: enumText(PUR.approved, item[PUR.approved]),
    approvedStage: enumText(PUR.approvedStage, item[PUR.approvedStage]),
    purpose: String(item[PUR.purpose] ?? '').trim(),
    comment: String(item[PUR.comment] ?? '').trim(),
    listUrl: String(item[PUR.listUrl] ?? '').trim(),
    utOrderNo: str(item[PUR.utOrderNo]),
  }
}

export interface CurrencyTotal {
  currency: string
  sum: number
  count: number
}

/**
 * Суммы страницы по валютам. Не смешиваем: у 1160 живут и рубли, и евро (проверено —
 * есть карточки с currencyId «EUR»), а «33 000 + 1 025» без разделения валют это ложь.
 */
export function totalsByCurrency(rows: Array<{ sum: number | null; currency: string }>): CurrencyTotal[] {
  const by = new Map<string, CurrencyTotal>()
  for (const r of rows) {
    if (r.sum === null) continue
    const acc = by.get(r.currency) || { currency: r.currency, sum: 0, count: 0 }
    acc.sum += r.sum
    acc.count += 1
    by.set(r.currency, acc)
  }
  return [...by.values()].sort((a, b) => b.sum - a.sum)
}

/** Карточка записи смарт-процесса в Bitrix: своих карточек 1160 в CRM нет. */
export const bitrixPayUrl = (id: string | number) =>
  `https://cp.cir.cx/crm/type/${PUR_TYPE_ID}/details/${id}/`

// ════════════════════════ ЧАСТЬ 2. Документы 1С УТ ════════════════════════

/** Пустая ссылка 1С. Ноль-guid означает «не заполнено», а не «объект с нулевым id». */
export const UT_ZERO = '00000000-0000-0000-0000-000000000000'

/** Дата 1С. «0001-01-01T00:00:00» — это НЕ дата, а незаполненное поле. */
export function utDay(v: unknown): string {
  const s = String(v ?? '')
  if (!s || s.startsWith('0001')) return ''
  return s.slice(0, 10)
}

/**
 * Сделка Bitrix по элементу Catalog_СделкиСКлиентами.
 *
 * Связь 1С ↔ Bitrix держится на том, что в `Description` этого справочника лежит НОМЕР
 * сделки Bitrix — так их заводит и 1С, и портал 55 (utDealEnsure). На живых данных
 * 26.07.2026 это верно для 1406 элементов из 1416: у остальных в Description название
 * товара («Кабель КГК 1х0,5…»), и превращать его в номер сделки нельзя. Поэтому берём
 * значение только когда оно ЦЕЛИКОМ номер, а не «содержит цифры».
 */
export function utDealNumber(description?: string | null): string {
  const s = String(description ?? '').trim()
  return /^\d{4,7}$/.test(s) ? s : ''
}

/** Статусы документов 1С → по-человечески. Неизвестное значение показываем как есть. */
const RU = (map: Record<string, string>) => (v?: string | null): string => {
  const s = String(v ?? '').trim()
  return s ? (map[s] || s) : ''
}

/** Статус заказа клиента. На живых данных: КОбеспечению 1291, НеСогласован 4. */
export const orderStatusRu = RU({
  НеСогласован: 'Не согласован',
  КОбеспечению: 'К обеспечению',
  КОтгрузке: 'К отгрузке',
  Закрыт: 'Закрыт',
})

/** Статус реализации. На живых данных: Отгружено 1257, КПредоплате 1. */
export const shipmentStatusRu = RU({
  КПредоплате: 'К предоплате',
  КОтгрузке: 'К отгрузке',
  Отгружено: 'Отгружено',
  Реализовано: 'Реализовано',
})

/** Статус заказа поставщику. На 28.07.2026: Закрыт 463, НеСогласован 10, Согласован 1. */
export const supplierStatusRu = RU({
  НеСогласован: 'Не согласован',
  Согласован: 'Согласован',
  Подтвержден: 'Подтверждён',
  КПоступлению: 'К поступлению',
  Закрыт: 'Закрыт / получен',
})

/**
 * Способ доставки. Значения взяты ИЗ ДАННЫХ, а не из документации 1С: в базе живут
 * «Самовывоз» (1293 заказа на 28.07.2026), «ДоКлиента» (12) и
 * «КПолучателюОпределяетСлужбаДоставки» (8).
 * Портал 55 в своей форме пишет другой набор («Доставка», «ДоставкаПеревозчиком») — он
 * тут не встречается ни разу, поэтому его значения оставлены в карте как есть.
 */
export const shipWayRu = RU({
  Самовывоз: 'Самовывоз',
  ДоКлиента: 'Доставка до клиента',
  КПолучателюОпределяетСлужбаДоставки: 'Перевозчик, адрес у службы доставки',
  Доставка: 'Доставка',
  ДоставкаПеревозчиком: 'Доставка перевозчиком',
  СиламиПоставщикаДоНашегоСклада: 'Поставщик привозит на наш склад',
  СамовывозСоСкладаПоставщика: 'Самовывоз со склада поставщика',
})

// ─────────── окна выборки ───────────
//
// Почему окно, а не «все документы». База 1С УТ начата 23.04.2026, и в ней
// на 28.07.2026 всего 1313 заказов клиента и 1283 реализации — влезло бы и целиком.
// Но раздел не должен ломаться в тот день, когда документов станет двадцать тысяч: окно
// по дате плюс ограничение сверху — это то, что уже проверено на портале (там $orderby
// по всей таблице заказов поставщику рвал соединение).

export const WINDOWS = [30, 90, 180] as const
export type WindowDays = (typeof WINDOWS)[number]
/** Сколько документов берём за раз. Дальше листаем локально, не дёргая 1С. */
export const DOC_LIMIT = 500

/** «ГГГГ-ММ-ДДT00:00:00» N дней назад — граница окна в формате 1С. */
export function windowFrom(days: number, now = new Date()): string {
  const d = new Date(now.getTime() - days * 86_400_000)
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T00:00:00`
}

/** Путь OData к документам за окно. Помеченные на удаление в 1С не наши данные. */
export function docWindowPath(entity: string, days: number, select: string[]): string {
  return odataPath(entity, {
    $filter: `Date ge datetime'${windowFrom(days)}' and DeletionMark eq false`,
    $orderby: 'Date desc',
    $select: select.join(','),
    $top: DOC_LIMIT,
    $inlinecount: 'allpages',
  })
}

/** Справочник «ref → название» пачками по 12 ссылок: длинный OR-фильтр 1С не принимает. */
export function refNamePaths(entity: string, refs: string[], select = 'Ref_Key,Description'): string[] {
  const uniq = [...new Set(refs.filter(isRef))]
  return chunk(uniq, 12).map((part) => odataPath(entity, {
    $filter: part.map((r) => `Ref_Key eq guid'${r}'`).join(' or '),
    $select: select,
    $top: 50,
  }))
}

/** Поиск контрагента по названию или ИНН — как на портале: ИНН точным равенством. */
export function contragentSearchPath(q: string): string | null {
  const s = q.trim()
  if (s.length < 3) return null
  const filter = /^\d{10}$|^\d{12}$/.test(s)
    ? `ИНН eq ${q1c(s)}`
    : `substringof(${q1c(s)},Description)`
  return odataPath('Catalog_Контрагенты', {
    $filter: filter, $select: 'Ref_Key,Description,ИНН', $top: 50,
  })
}

// ─────────── сырые документы 1С ───────────

export interface UtOrderRaw {
  Ref_Key: string
  Number?: string
  Date?: string
  Статус?: string
  Posted?: boolean
  СуммаДокумента?: number | string
  Сделка_Key?: string
  Склад_Key?: string
  Контрагент_Key?: string
  Менеджер_Key?: string
  СпособДоставки?: string
  АдресДоставки?: string
  ДатаОтгрузки?: string
}

export interface UtShipmentRaw extends UtOrderRaw {
  /** Ссылка на заказ клиента. Заполнена у 935 реализаций из 1283 (на 28.07.2026). */
  ЗаказКлиента?: string
}

export interface UtSupplierOrderRaw {
  Ref_Key: string
  Number?: string
  Date?: string
  Статус?: string
  Posted?: boolean
  СуммаДокумента?: number | string
  Партнер_Key?: string
  Склад_Key?: string
  Комментарий?: string
  НомерПоДаннымПоставщика?: string
  ДатаПоступления?: string
  ЖелаемаяДатаПоступления?: string
}

/** Общая шапка обоих документов отгрузки. */
const SHIP_COMMON_SELECT = [
  'Ref_Key', 'Number', 'Date', 'Статус', 'Posted', 'СуммаДокумента', 'Сделка_Key',
  'Склад_Key', 'Контрагент_Key', 'Менеджер_Key', 'СпособДоставки', 'АдресДоставки',
]

/** У заказа клиента есть плановая дата отгрузки — задача логиста. */
export const ORDER_SELECT = [...SHIP_COMMON_SELECT, 'ДатаОтгрузки']

/**
 * У реализации поля `ДатаОтгрузки` НЕТ — 1С отвечает «Сегмент пути ДатаОтгрузки не найден»
 * и роняет весь запрос. Дата отгрузки у реализации — это её собственная `Date`.
 */
export const SHIPMENT_SELECT = [...SHIP_COMMON_SELECT, 'ЗаказКлиента']

export const SUPPLIER_SELECT = [
  'Ref_Key', 'Number', 'Date', 'Статус', 'Posted', 'СуммаДокумента', 'Партнер_Key',
  'Склад_Key', 'Комментарий', 'НомерПоДаннымПоставщика', 'ДатаПоступления', 'ЖелаемаяДатаПоступления',
]

/** Названия по ссылкам: склады, сделки 1С, контрагенты, партнёры, менеджеры. */
export interface UtNames {
  warehouse: Record<string, string>
  /** Ref сделки 1С → Description (номер сделки Bitrix или свободный текст). */
  deal: Record<string, string>
  contragent: Record<string, string>
  partner: Record<string, string>
  user: Record<string, string>
}

export const emptyUtNames = (): UtNames =>
  ({ warehouse: {}, deal: {}, contragent: {}, partner: {}, user: {} })

const name = (map: Record<string, string>, ref?: string): string =>
  (ref && ref !== UT_ZERO && map[ref]) || ''

/** Строка отгрузки или заказа клиента. */
export interface ShipRow {
  ref: string
  /** 'order' — заказ клиента (ещё не отгружен), 'shipment' — реализация. */
  kind: 'order' | 'shipment'
  number: string
  day: string
  status: string
  posted: boolean
  sum: number
  client: string
  clientRef: string
  warehouse: string
  manager: string
  /** Номер сделки Bitrix, если он читается из справочника сделок 1С. */
  dealId: string
  /** Description сделки 1С, когда это не номер (тогда показываем как есть). */
  dealText: string
  way: string
  address: string
  /** Дата отгрузки из заказа: план, а не факт. Пусто — не указана. */
  shipDay: string
  /** Дней от плановой даты отгрузки до сегодня. >0 — срок прошёл. null — срока нет. */
  shipOverdueDays: number | null
  /** Ссылка на заказ клиента (только у реализаций). */
  orderRef: string
  /** Номер заказа клиента, если он есть в загруженном окне. */
  orderNumber: string
}

function shipCommon(d: UtOrderRaw, names: UtNames): Omit<ShipRow, 'kind' | 'status' | 'shipDay' | 'shipOverdueDays' | 'orderRef' | 'orderNumber'> {
  const dealDesc = name(names.deal, d.Сделка_Key)
  const dealId = utDealNumber(dealDesc)
  return {
    ref: d.Ref_Key,
    number: String(d.Number ?? '').trim(),
    day: utDay(d.Date),
    posted: d.Posted === true,
    sum: toNumber(d.СуммаДокумента) ?? 0,
    client: name(names.contragent, d.Контрагент_Key),
    clientRef: d.Контрагент_Key && d.Контрагент_Key !== UT_ZERO ? d.Контрагент_Key : '',
    warehouse: name(names.warehouse, d.Склад_Key),
    manager: name(names.user, d.Менеджер_Key),
    dealId,
    dealText: dealId ? '' : dealDesc,
    way: shipWayRu(d.СпособДоставки),
    address: String(d.АдресДоставки ?? '').trim(),
  }
}

export function toShipment(d: UtShipmentRaw, names: UtNames, orderNumbers: Record<string, string>): ShipRow {
  const orderRef = d.ЗаказКлиента && d.ЗаказКлиента !== UT_ZERO ? d.ЗаказКлиента : ''
  return {
    ...shipCommon(d, names),
    kind: 'shipment',
    status: shipmentStatusRu(d.Статус),
    shipDay: '',
    shipOverdueDays: null,
    orderRef,
    orderNumber: (orderRef && orderNumbers[orderRef]) || '',
  }
}

export function toOrder(d: UtOrderRaw, names: UtNames, today = todayIso()): ShipRow {
  const shipDay = utDay(d.ДатаОтгрузки)
  return {
    ...shipCommon(d, names),
    kind: 'order',
    status: orderStatusRu(d.Статус),
    shipDay,
    shipOverdueDays: shipDay ? daysPast(shipDay, today) : null,
    orderRef: '',
    orderNumber: '',
  }
}

/** Строка «заказ поставщику» из 1С. */
export interface SupplyRow {
  ref: string
  number: string
  day: string
  status: string
  statusKey: string
  posted: boolean
  sum: number
  supplier: string
  supplierRef: string
  warehouse: string
  /** Номер счёта по данным поставщика — им заявка 1160 и опознаётся глазами. */
  supplierInvoiceNo: string
  /** Дата поступления: заполнена — товар оприходован. */
  arrivedDay: string
  /** Желаемая дата поступления (план). */
  wantedDay: string
  comment: string
  /** Номер сделки Bitrix из комментария: другой связи у заказа поставщику нет. */
  dealId: string
}

/**
 * Сделка из комментария к заказу поставщику.
 *
 * Полагаться на поле сделки у этого документа нельзя: `Сделка_Key` нулевой у 466 записей
 * из 473 на 28.07.2026, а закупщик пишет номер в комментарий свободным текстом —
 * «Сд. 104138», «сделка 109705», «под сделку 93035», «к сделке 97035». Поэтому и регулярка
 * широкая: слово с корнем «сделк» или сокращение «сд» плюс номер. Ничего не совпало —
 * сделки нет, и мы её не выдумываем.
 */
export function dealFromComment(comment?: string | null): string {
  const s = String(comment ?? '')
  const m = /(?:сделк\p{L}*|сд\.?)\s*№?\s*#?\s*(\d{4,7})/iu.exec(s)
  return m ? m[1] : ''
}

export function toSupplyRow(d: UtSupplierOrderRaw, names: UtNames): SupplyRow {
  return {
    ref: d.Ref_Key,
    number: String(d.Number ?? '').trim(),
    day: utDay(d.Date),
    status: supplierStatusRu(d.Статус),
    statusKey: String(d.Статус ?? ''),
    posted: d.Posted === true,
    sum: toNumber(d.СуммаДокумента) ?? 0,
    supplier: name(names.partner, d.Партнер_Key),
    supplierRef: d.Партнер_Key && d.Партнер_Key !== UT_ZERO ? d.Партнер_Key : '',
    warehouse: name(names.warehouse, d.Склад_Key),
    supplierInvoiceNo: String(d.НомерПоДаннымПоставщика ?? '').trim(),
    arrivedDay: utDay(d.ДатаПоступления),
    wantedDay: utDay(d.ЖелаемаяДатаПоступления),
    comment: String(d.Комментарий ?? '').trim(),
    dealId: dealFromComment(d.Комментарий),
  }
}

/**
 * Заказы клиента, по которым реализации в окне нет — «ждёт отгрузки».
 *
 * Оговорка, которую экран обязан повторить человеку: реализация ищется ТОЛЬКО среди
 * загруженного окна. Реализация всегда позже своего заказа, поэтому для заказа из окна
 * её отсутствие в том же окне почти всегда правда; «почти» — потому что
 * на 28.07.2026 у 348 реализаций из 1283 ссылки на заказ нет вовсе (в 1С их оформили
 * не «на основании»), и такой заказ останется в списке ожидающих, хотя товар уже уехал.
 */
export function pendingOrders(orders: ShipRow[], shipments: ShipRow[]): ShipRow[] {
  const shipped = new Set(shipments.map((s) => s.orderRef).filter(Boolean))
  return orders.filter((o) => !shipped.has(o.ref))
}

/** Сколько реализаций окна не сослались на заказ — для честной оговорки на экране. */
export const shipmentsWithoutOrder = (shipments: ShipRow[]): number =>
  shipments.filter((s) => !s.orderRef).length

/** Сумма и количество по строкам. У документов 1С валюта одна (рубли), делить не надо. */
export function sumRows(rows: Array<{ sum: number }>): { sum: number; count: number } {
  return { sum: rows.reduce((a, r) => a + r.sum, 0), count: rows.length }
}
